/**
 * 小红书请求签名生成工具 TypeScript 版本
 * 完全对应 Python 版本的功能实现
 * 兼容ES5/ES2015版本
 */

import * as crypto from 'node:crypto';

// ========== 配置常量类 ==========
export class CryptoConfig {
    // 位操作相关常量
    static readonly MAX_32BIT = 0xFFFFFFFF;
    static readonly MAX_SIGNED_32BIT = 0x7FFFFFFF;
    static readonly MAX_BYTE = 255;

    // Base64编码相关常量
    static readonly STANDARD_BASE64_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
    static readonly CUSTOM_BASE64_ALPHABET = "ZmserbBoHQtNP+wOcza/LpngG8yJq42KWYj0DSfdikx3VT16IlUAFM97hECvuRX5";
    static readonly X3_BASE64_ALPHABET = "MfgqrsbcyzPQRStuvC7mn501HIJBo2DEFTKdeNOwxWXYZap89+/A4UVLhijkl63G";

    // XOR 密钥 (124字节)
    static readonly HEX_KEY = "71a302257793271ddd273bcee3e4b98d9d7935e1da33f5765e2ea8afb6dc77a51a499d23b67c20660025860cbf13d4540d92497f58686c574e508f46e1956344f39139bf4faf22a3eef120b79258145b2feb5193b6478669961298e79bedca646e1a693a926154a5a7a1bd1cf0dedb742f917a747a1e388b234f2277";

    // 十六进制处理相关常量
    static readonly EXPECTED_HEX_LENGTH = 32;
    static readonly OUTPUT_BYTE_COUNT = 8;
    static readonly HEX_CHUNK_SIZE = 2;

    // 载荷构建相关常量 (固定124字节)
    static readonly VERSION_BYTES = [119, 104, 96, 41];
    static readonly SEQUENCE_VALUE_MIN = 15;
    static readonly SEQUENCE_VALUE_MAX = 50;
    static readonly WINDOW_PROPS_LENGTH_MIN = 900;
    static readonly WINDOW_PROPS_LENGTH_MAX = 1200;

    // 校验和常量
    static readonly CHECKSUM_VERSION = 1;
    static readonly CHECKSUM_XOR_KEY = 115;
    static readonly CHECKSUM_FIXED_TAIL = [249, 65, 103, 103, 201, 181, 131, 99, 94, 7, 68, 250, 132, 21];

    // 环境指纹生成参数
    static readonly ENV_FINGERPRINT_XOR_KEY = 41;
    static readonly ENV_FINGERPRINT_TIME_OFFSET_MIN = 10;
    static readonly ENV_FINGERPRINT_TIME_OFFSET_MAX = 50;

    // 签名数据模板
    static readonly SIGNATURE_DATA_TEMPLATE = {
        x0: "4.2.6",
        x1: "xhs-pc-web",
        x2: "Windows",
        x3: "",
        x4: ""
    };

    // 前缀常量
    static readonly X3_PREFIX = "mns0301_";
    static readonly XYS_PREFIX = "XYS_";

    // Trace ID 生成常量
    static readonly HEX_CHARS = "abcdef0123456789";
    static readonly XRAY_TRACE_ID_SEQ_MAX = 8388607; // 2^23-1
    static readonly XRAY_TRACE_ID_TIMESTAMP_SHIFT = 23;
    static readonly B3_TRACE_ID_LENGTH = 16;

    // 新增 PUBLIC_USERAGENT
    static readonly PUBLIC_USERAGENT = "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/142.0.0.0 Safari/537.36 Edg/142.0.0.0";

    // 新增 SIGNATURE_XSCOMMON_TEMPLATE
    static readonly SIGNATURE_XSCOMMON_TEMPLATE = {
        s0: 5,
        s1: "",
        x0: "1",
        x1: "4.2.6",
        x2: "Windows",
        x3: "xhs-pc-web",
        x4: "4.86.0",
        x5: "",
        x6: "",
        x7: "",
        x8: "",
        x9: -596800761,
        x10: 0,
        x11: "normal"
    };
}

// ========== 位操作类 ==========
export class BitOperations {
    private config = CryptoConfig;

    normalizeTo32bit(value: number): number {
        return value & this.config.MAX_32BIT;
    }

    xorTransformArray(inputArray: number[]): Uint8Array {
        const keyBytes = this.hexStringToBytes(this.config.HEX_KEY);
        const result = new Uint8Array(inputArray.length);

        for (let i = 0; i < inputArray.length; i++) {
            result[i] = inputArray[i] ^ keyBytes[i % keyBytes.length];
        }

        return result;
    }

    private hexStringToBytes(hexString: string): number[] {
        const bytes: number[] = [];
        for (let i = 0; i < hexString.length; i += 2) {
            bytes.push(parseInt(hexString.substr(i, 2), 16));
        }
        return bytes;
    }
}

// ========== 随机数生成类 ==========
export class RandomGenerator {
    private config = CryptoConfig;

    generateRandomBytes(byteCount: number): number[] {
        const result: number[] = [];
        for (let i = 0; i < byteCount; i++) {
            result.push(Math.floor(Math.random() * (this.config.MAX_BYTE + 1)));
        }
        return result;
    }

    generateRandomByteInRange(minVal: number, maxVal: number): number {
        return Math.floor(Math.random() * (maxVal - minVal + 1)) + minVal;
    }

    generateRandomInt(): number {
        return Math.floor(Math.random() * (this.config.MAX_32BIT + 1));
    }

    generateB3TraceId(): string {
        let result = '';
        for (let i = 0; i < this.config.B3_TRACE_ID_LENGTH; i++) {
            result += this.config.HEX_CHARS[Math.floor(Math.random() * this.config.HEX_CHARS.length)];
        }
        return result;
    }

    generateXrayTraceId(timestamp?: number, seq?: number): string {
        if (timestamp === undefined) {
            timestamp = Date.now();
        }
        if (seq === undefined) {
            seq = Math.floor(Math.random() * (this.config.XRAY_TRACE_ID_SEQ_MAX + 1));
        }

        const part1 = ((timestamp << this.config.XRAY_TRACE_ID_TIMESTAMP_SHIFT) | seq).toString(16).padStart(16, '0');

        let part2 = '';
        for (let i = 0; i < 16; i++) {
            part2 += this.config.HEX_CHARS[Math.floor(Math.random() * this.config.HEX_CHARS.length)];
        }

        return part1 + part2;
    }
}

// ========== Base64编码器 ==========
export class Base64Encoder {
    private config = CryptoConfig;

    encode(dataToEncode: string): string {
        const dataBytes = Buffer.from(dataToEncode, 'utf-8');
        const standardEncodedString = dataBytes.toString('base64');

        let result = '';
        for (const char of standardEncodedString) {
            const index = this.config.STANDARD_BASE64_ALPHABET.indexOf(char);
            if (index !== -1) {
                result += this.config.CUSTOM_BASE64_ALPHABET[index];
            } else {
                result += char;
            }
        }

        return result;
    }

    decode(encodedString: string): string {
        let standardString = '';
        for (const char of encodedString) {
            const index = this.config.CUSTOM_BASE64_ALPHABET.indexOf(char);
            if (index !== -1) {
                standardString += this.config.STANDARD_BASE64_ALPHABET[index];
            } else {
                standardString += char;
            }
        }

        return Buffer.from(standardString, 'base64').toString('utf-8');
    }

    encodeX3(dataBytes: Uint8Array): string {
        const base64String = Buffer.from(dataBytes).toString('base64');

        let result = '';
        for (const char of base64String) {
            const index = this.config.STANDARD_BASE64_ALPHABET.indexOf(char);
            if (index !== -1) {
                result += this.config.X3_BASE64_ALPHABET[index];
            } else {
                result += char;
            }
        }

        return result;
    }

    decodeX3(encodedString: string): Uint8Array {
        let standardString = '';
        for (const char of encodedString) {
            const index = this.config.X3_BASE64_ALPHABET.indexOf(char);
            if (index !== -1) {
                standardString += this.config.STANDARD_BASE64_ALPHABET[index];
            } else {
                standardString += char;
            }
        }

        return new Uint8Array(Buffer.from(standardString, 'base64'));
    }
}

// ========== 请求签名验证器 ==========
export type HttpMethod = 'GET' | 'POST';

export class RequestSignatureValidator {
    static validateMethod(method: any): HttpMethod {
        if (typeof method !== 'string') {
            throw new TypeError(`method must be str, got ${typeof method}`);
        }

        const normalizedMethod = method.trim().toUpperCase() as HttpMethod;
        if (!['GET', 'POST'].includes(normalizedMethod)) {
            throw new Error(`method must be 'GET' or 'POST', got '${method}'`);
        }

        return normalizedMethod;
    }

    static validateUri(uri: any): string {
        if (typeof uri !== 'string') {
            throw new TypeError(`uri must be str, got ${typeof uri}`);
        }

        if (!uri.trim()) {
            throw new Error('uri cannot be empty');
        }

        return uri.trim();
    }

    static validateA1Value(a1Value: any): string {
        if (typeof a1Value !== 'string') {
            throw new TypeError(`a1_value must be str, got ${typeof a1Value}`);
        }

        if (!a1Value.trim()) {
            throw new Error('a1_value cannot be empty');
        }

        return a1Value.trim();
    }

    static validateXsecAppid(xsecAppid: any): string {
        if (typeof xsecAppid !== 'string') {
            throw new TypeError(`xsec_appid must be str, got ${typeof xsecAppid}`);
        }

        if (!xsecAppid.trim()) {
            throw new Error('xsec_appid cannot be empty');
        }

        return xsecAppid.trim();
    }

    static validatePayload(payload: any): Record<string, any> | null {
        if (payload !== null && (typeof payload !== 'object' || Array.isArray(payload))) {
            throw new TypeError(`payload must be dict or None, got ${typeof payload}`);
        }

        if (payload !== null) {
            for (const key in payload) {
                if (typeof key !== 'string') {
                    throw new TypeError(`payload keys must be str, got ${typeof key} for key '${key}'`);
                }
            }
        }

        return payload;
    }
}

// ========== 加密处理器 ==========
export class CryptoProcessor {
    public config = CryptoConfig;
    public bitOps = new BitOperations();
    public b64encoder = new Base64Encoder();
    public randomGen = new RandomGenerator();

    private intToLeBytes(val: number, length: number = 4): number[] {
        const arr: number[] = [];
        for (let i = 0; i < length; i++) {
            arr.push(val & 0xFF);
            val >>>= 8;
        }
        return arr;
    }

    private envFingerprintA(ts: number, xorKey: number): number[] {
        const data: number[] = [];

        // 小端序编码时间戳
        for (let i = 0; i < 8; i++) {
            data.push((ts >>> (i * 8)) & 0xFF);
        }

        // 计算校验和
        const sum1 = data.slice(1, 5).reduce((a, b) => a + b, 0);
        const sum2 = data.slice(5, 8).reduce((a, b) => a + b, 0);
        const mark = ((sum1 & 0xFF) + sum2) & 0xFF;
        data[0] = mark;

        // XOR变换
        return data.map(b => b ^ xorKey);
    }

    private envFingerprintB(ts: number): number[] {
        const data: number[] = [];
        for (let i = 0; i < 8; i++) {
            data.push((ts >>> (i * 8)) & 0xFF);
        }
        return data;
    }

    buildPayloadArray(
        hexParameter: string,
        a1Value: string,
        appIdentifier: string = "xhs-pc-web",
        stringParam: string = "",
        timestamp?: number
    ): number[] {
        const payload: number[] = [];

        // 固定头
        payload.push(...this.config.VERSION_BYTES);

        // 随机种子
        const seed = this.randomGen.generateRandomInt();
        const seedBytes = this.intToLeBytes(seed, 4);
        payload.push(...seedBytes);
        const seedByte0 = seedBytes[0];

        // 时间戳处理
        if (timestamp === undefined) {
            timestamp = Date.now() / 1000;
        }
        const tsMs = Math.floor(timestamp * 1000);

        payload.push(...this.envFingerprintA(tsMs, this.config.ENV_FINGERPRINT_XOR_KEY));

        const timeOffset = this.randomGen.generateRandomByteInRange(
            this.config.ENV_FINGERPRINT_TIME_OFFSET_MIN,
            this.config.ENV_FINGERPRINT_TIME_OFFSET_MAX
        );
        payload.push(...this.envFingerprintB(tsMs - timeOffset * 1000));

        // 序列值和窗口属性
        const sequenceValue = this.randomGen.generateRandomByteInRange(
            this.config.SEQUENCE_VALUE_MIN,
            this.config.SEQUENCE_VALUE_MAX
        );
        payload.push(...this.intToLeBytes(sequenceValue, 4));

        const windowPropsLength = this.randomGen.generateRandomByteInRange(
            this.config.WINDOW_PROPS_LENGTH_MIN,
            this.config.WINDOW_PROPS_LENGTH_MAX
        );
        payload.push(...this.intToLeBytes(windowPropsLength, 4));

        // URI 长度
        const uriLength = Buffer.from(stringParam, 'utf-8').length;
        payload.push(...this.intToLeBytes(uriLength, 4));

        // MD5 XOR 段
        const md5Bytes = Buffer.from(hexParameter, 'hex');
        for (let i = 0; i < 8; i++) {
            payload.push(md5Bytes[i] ^ seedByte0);
        }

        // A1 长度和内容 (固定52字节)
        payload.push(52);
        const a1Bytes = Buffer.from(a1Value, 'utf-8');
        for (let i = 0; i < 52; i++) {
            payload.push(i < a1Bytes.length ? a1Bytes[i] : 0);
        }

        // 源长度和内容 (固定10字节)
        payload.push(10);
        const sourceBytes = Buffer.from(appIdentifier, 'utf-8');
        for (let i = 0; i < 10; i++) {
            payload.push(i < sourceBytes.length ? sourceBytes[i] : 0);
        }

        // 尾部校验
        payload.push(1);
        payload.push(this.config.CHECKSUM_VERSION);
        payload.push(seedByte0 ^ this.config.CHECKSUM_XOR_KEY);
        payload.push(...this.config.CHECKSUM_FIXED_TAIL);

        return payload;
    }
}

// ========== CRC32 加密类 ==========
export class CRC32 {
    private static readonly CRC32_TABLE: number[] = (() => {
        const table: number[] = [];
        for (let i = 0; i < 256; i++) {
            let crc = i;
            for (let j = 0; j < 8; j++) {
                crc = (crc & 1) ? (0xEDB88320 ^ (crc >>> 1)) : (crc >>> 1);
            }
            table.push(crc >>> 0);
        }
        return table;
    })();

    static crc32JsInt(input: string | number[] | Uint8Array, signed: boolean = true): number {
        let crc = 0xFFFFFFFF;
        const data = typeof input === 'string' ? this.stringToBytes(input) : input;

        for (const byte of data) {
            const index = (crc ^ byte) & 0xFF;
            crc = (this.CRC32_TABLE[index] ^ (crc >>> 8)) >>> 0;
        }

        crc = (crc ^ 0xFFFFFFFF) >>> 0;

        if (signed) {
            return crc > 0x7FFFFFFF ? crc - 0x100000000 : crc;
        }
        return crc;
    }

    private static stringToBytes(str: string): number[] {
        const bytes: number[] = [];
        for (let i = 0; i < str.length; i++) {
            const code = str.charCodeAt(i);
            if (code <= 0xFF) {
                bytes.push(code);
            } else {
                // JS 字符串模式：只取低8位
                bytes.push(code & 0xFF);
            }
        }
        return bytes;
    }
}

// ========== 指纹数据常量 ==========
export class FingerprintData {
    // GPU 供应商列表
    static readonly GPU_VENDORS = [
        "Google Inc. (Intel)|ANGLE (Intel, Intel(R) HD Graphics 400 (0x00000166) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (Intel)|ANGLE (Intel, Intel(R) HD Graphics 4400 (0x00001112) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (Intel)|ANGLE (Intel, Intel(R) HD Graphics 4600 (0x00000412) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (Intel)|ANGLE (Intel, Intel(R) HD Graphics 520 (0x1912) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (Intel)|ANGLE (Intel, Intel(R) HD Graphics 530 (0x00001912) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (Intel)|ANGLE (Intel, Intel(R) HD Graphics 550 (0x00001512) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (Intel)|ANGLE (Intel, Intel(R) HD Graphics 6000 (0x1606) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (Intel)|ANGLE (Intel, Intel(R) Iris(TM) Graphics 540 (0x1912) Direct3D11 vs_5_0 ps_5_0, D3D11)",  // noqa: E501
        "Google Inc. (Intel)|ANGLE (Intel, Intel(R) Iris(TM) Graphics 550 (0x1913) Direct3D11 vs_5_0 ps_5_0, D3D11)",  // noqa: E501
        "Google Inc. (Intel)|ANGLE (Intel, Intel(R) Iris(TM) Plus Graphics 640 (0x161C) Direct3D11 vs_5_0 ps_5_0, D3D11)",  // noqa: E501
        "Google Inc. (Intel)|ANGLE (Intel, Intel(R) UHD Graphics 600 (0x3E80) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (Intel)|ANGLE (Intel, Intel(R) UHD Graphics 620 (0x00003EA0) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (Intel)|ANGLE (Intel, Intel(R) UHD Graphics 630 (0x00003E9B) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (Intel)|ANGLE (Intel, Intel(R) UHD Graphics 655 (0x00009BC8) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (Intel)|ANGLE (Intel, Intel(R) Iris(R) Xe Graphics (0x000046A8) Direct3D11 vs_5_0 ps_5_0, D3D11)",  // noqa: E501
        "Google Inc. (Intel)|ANGLE (Intel, Intel(R) Iris(R) Xe Graphics (0x00009A49) Direct3D11 vs_5_0 ps_5_0, D3D11)",  // noqa: E501
        "Google Inc. (Intel)|ANGLE (Intel, Intel(R) Iris(R) Xe MAX Graphics (0x00009BC0) Direct3D11 vs_5_0 ps_5_0, D3D11)",  // noqa: E501
        "Google Inc. (Intel)|ANGLE (Intel, Intel Arc A370M (0x0000AF51) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (Intel)|ANGLE (Intel, Intel Arc A380 (0x0000AF41) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (Intel)|ANGLE (Intel, Intel Arc A380M (0x0000AF5E) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (Intel)|ANGLE (Intel, Intel Arc A550 (0x0000AF42) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (Intel)|ANGLE (Intel, Intel Arc A770 (0x0000AF43) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (Intel)|ANGLE (Intel, Intel Arc A770M (0x0000AF50) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (Intel)|ANGLE (Intel, Mesa Intel(R) Graphics (RPL‑P GT1) (0x0000A702) OpenGL 4.6)",
        "Google Inc. (Intel)|ANGLE (Intel, Mesa Intel(R) UHD Graphics 770 (0x00004680) OpenGL 4.6)",
        "Google Inc. (Intel)|ANGLE (Intel, Mesa Intel(R) HD Graphics 4400 (0x00001122) OpenGL 4.6)",
        "Google Inc. (Intel)|ANGLE (Intel, Mesa Intel(R) Graphics (ADL‑S GT1) (0x0000A0A1) OpenGL 4.6)",
        "Google Inc. (Intel)|ANGLE (Intel, Mesa Intel(R) Graphics (RKL GT1) (0x0000A9A1) OpenGL 4.6)",
        "Google Inc. (Intel)|ANGLE (Intel, Mesa Intel(R) UHD Graphics (CML GT2) (0x00009A14) OpenGL 4.6)",
        "Google Inc. (Intel)|ANGLE (Intel, Intel(R) HD Graphics 3000 (0x00001022) Direct3D9Ex vs_3_0 ps_3_0, igdumd64.dll)",  // noqa: E501
        "Google Inc. (Intel)|ANGLE (Intel, Intel(R) HD Graphics Family (0x00000A16) Direct3D11 vs_5_0 ps_5_0, D3D11)",  // noqa: E501
        "Google Inc. (Intel)|ANGLE (Intel, Intel(R) Iris Pro OpenGL Engine, OpenGL 4.1)",
        "Google Inc. (Intel)|ANGLE (Intel, Intel(R) Iris(TM) Plus Graphics 645 (0x1616) Direct3D11 vs_5_0 ps_5_0, D3D11)",  // noqa: E501
        "Google Inc. (Intel)|ANGLE (Intel, Intel(R) Iris(TM) Plus Graphics 655 (0x161E) Direct3D11 vs_5_0 ps_5_0, D3D11)",  // noqa: E501
        "Google Inc. (Intel)|ANGLE (Intel, Intel(R) UHD Graphics 730 (0x0000A100) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (Intel)|ANGLE (Intel, Intel(R) UHD Graphics 805 (0x0000B0A0) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (AMD)|ANGLE (AMD, AMD Radeon Vega 3 Graphics (0x000015E0) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (AMD)|ANGLE (AMD, AMD Radeon Vega 8 Graphics (0x000015D8) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (AMD)|ANGLE (AMD, AMD Radeon Vega 11 Graphics (0x000015DD) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (AMD)|ANGLE (AMD, AMD Radeon Graphics (0x00001636) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (AMD)|ANGLE (AMD, AMD Radeon RX 5500 XT Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (AMD)|ANGLE (AMD, AMD Radeon RX 560 (0x000067EF) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (AMD)|ANGLE (AMD, AMD Radeon RX 570 (0x000067DF) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (AMD)|ANGLE (AMD, AMD Radeon RX 580 2048SP (0x00006FDF) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (AMD)|ANGLE (AMD, AMD Radeon RX 590 (0x000067FF) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (AMD)|ANGLE (AMD, AMD Radeon RX 6600 (0x000073FF) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (AMD)|ANGLE (AMD, AMD Radeon RX 6600 XT (0x000073FF) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (AMD)|ANGLE (AMD, AMD Radeon RX 6650 XT Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (AMD)|ANGLE (AMD, AMD Radeon RX 6700 XT (0x000073DF) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (AMD)|ANGLE (AMD, AMD Radeon RX 6800 (0x000073BF) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (AMD)|ANGLE (AMD, AMD Radeon RX 6900 XT (0x000073C2) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (AMD)|ANGLE (AMD, AMD Radeon RX 7700 XT Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (AMD)|ANGLE (AMD, AMD Radeon Pro 5300M OpenGL Engine, OpenGL 4.1)",
        "Google Inc. (AMD)|ANGLE (AMD, AMD Radeon Pro 5500 XT OpenGL Engine, OpenGL 4.1)",
        "Google Inc. (AMD)|ANGLE (AMD, AMD Radeon R7 370 Series (0x00006811) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (AMD)|ANGLE (AMD, ATI Technologies Inc. AMD Radeon RX Vega 64 OpenGL Engine, OpenGL 4.1)",
        "Google Inc. (NVIDIA)|ANGLE (NVIDIA, NVIDIA GeForce GTX 1050 (0x00001C81) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (NVIDIA)|ANGLE (NVIDIA, NVIDIA GeForce GTX 1050 Ti (0x00001C8C) Direct3D11 vs_5_0 ps_5_0, D3D11)",  // noqa: E501
        "Google Inc. (NVIDIA)|ANGLE (NVIDIA, NVIDIA GeForce GTX 1060 6GB (0x000010DE) Direct3D11 vs_5_0 ps_5_0, D3D11)",  // noqa: E501
        "Google Inc. (NVIDIA)|ANGLE (NVIDIA, NVIDIA GeForce GTX 1070 (0x00001B81) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (NVIDIA)|ANGLE (NVIDIA, NVIDIA GeForce GTX 1080 (0x00001B80) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (NVIDIA)|ANGLE (NVIDIA, NVIDIA GeForce RTX 2060 (0x00001F06) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (NVIDIA)|ANGLE (NVIDIA, NVIDIA GeForce RTX 2060 SUPER (0x00001F06) Direct3D11 vs_5_0 ps_5_0, D3D11)",  // noqa: E501
        "Google Inc. (NVIDIA)|ANGLE (NVIDIA, NVIDIA GeForce RTX 2070 (0x00001F10) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (NVIDIA)|ANGLE (NVIDIA, NVIDIA GeForce RTX 2070 SUPER (0x00001F10) Direct3D11 vs_5_0 ps_5_0, D3D11)",  // noqa: E501
        "Google Inc. (NVIDIA)|ANGLE (NVIDIA, NVIDIA GeForce RTX 3060 (0x0000250F) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (NVIDIA)|ANGLE (NVIDIA, NVIDIA GeForce RTX 3060 Ti (0x00002489) Direct3D11 vs_5_0 ps_5_0, D3D11)",  // noqa: E501
        "Google Inc. (NVIDIA)|ANGLE (NVIDIA, NVIDIA GeForce RTX 3070 (0x00002488) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (NVIDIA)|ANGLE (NVIDIA, NVIDIA GeForce RTX 3070 Ti (0x000028A5) Direct3D11 vs_5_0 ps_5_0, D3D11)",  // noqa: E501
        "Google Inc. (NVIDIA)|ANGLE (NVIDIA, NVIDIA GeForce RTX 3080 (0x00002206) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (NVIDIA)|ANGLE (NVIDIA, NVIDIA GeForce RTX 3080 Ti (0x00002208) Direct3D11 vs_5_0 ps_5_0, D3D11)",  // noqa: E501
        "Google Inc. (NVIDIA)|ANGLE (NVIDIA, NVIDIA GeForce RTX 3090 (0x00002204) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (NVIDIA)|ANGLE (NVIDIA, NVIDIA GeForce RTX 4060 (0x00002882) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (NVIDIA)|ANGLE (NVIDIA, NVIDIA GeForce RTX 4060 Ti (0x00002803) Direct3D11 vs_5_0 ps_5_0, D3D11)",  // noqa: E501
        "Google Inc. (NVIDIA)|ANGLE (NVIDIA, NVIDIA GeForce RTX 4070 (0x00002786) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (NVIDIA)|ANGLE (NVIDIA, NVIDIA GeForce RTX 4070 Ti (0x00002857) Direct3D11 vs_5_0 ps_5_0, D3D11)",  // noqa: E501
        "Google Inc. (NVIDIA)|ANGLE (NVIDIA, NVIDIA GeForce RTX 4080 (0x00002819) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (NVIDIA)|ANGLE (NVIDIA, NVIDIA GeForce RTX 4090 (0x00002684) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (NVIDIA)|ANGLE (NVIDIA, NVIDIA Quadro RTX 5000 Ada Generation (0x000026B2) Direct3D11 vs_5_0 ps_5_0, D3D11)",  // noqa: E501
        "Google Inc. (NVIDIA)|ANGLE (NVIDIA, NVIDIA Quadro P400 (0x00001CB3) Direct3D11 vs_5_0 ps_5_0, D3D11)",
        "Google Inc. (Google)|ANGLE (Google, Vulkan 1.3.0 (SwiftShader Device (Subzero) (0x0000C0DE)), SwiftShader driver)",  // noqa: E501
        "Google Inc. (Google)|ANGLE (Google, Vulkan 1.3.0 (SwiftShader Device (Subzero)), SwiftShader driver)",
        "Google Inc. (Google)|ANGLE (Google, Vulkan 1.3.0 (SwiftShader Device), SwiftShader driver)",
    ];

    // 屏幕分辨率配置
    static readonly SCREEN_RESOLUTIONS = {
        resolutions: ["1366;768", "1600;900", "1920;1080", "2560;1440", "3840;2160", "7680;4320"],
        weights: [0.25, 0.15, 0.35, 0.15, 0.08, 0.02]
    };

    // 颜色深度选项
    static readonly COLOR_DEPTH_OPTIONS = {
        values: [16, 24, 30, 32],
        weights: [0.05, 0.6, 0.05, 0.3]
    };

    // 设备内存选项
    static readonly DEVICE_MEMORY_OPTIONS = {
        values: [1, 2, 4, 8, 12, 16],
        weights: [0.10, 0.25, 0.4, 0.2, 0.03, 0.01]
    };

    // CPU 核心数选项
    static readonly CORE_OPTIONS = {
        values: [2, 4, 6, 8, 12, 16, 24, 32],
        weights: [0.1, 0.4, 0.2, 0.15, 0.08, 0.04, 0.02, 0.01]
    };

    // 浏览器插件
    static readonly BROWSER_PLUGINS = "PDF Viewer,Chrome PDF Viewer,Chromium PDF Viewer,Microsoft Edge PDF Viewer,WebKit built-in PDF";

    // Canvas 哈希
    static readonly CANVAS_HASH = "742cc32c";

    // 语音哈希选项
    static readonly VOICE_HASH_OPTIONS = "10311144241322244122";

    // 字体字符串
    static readonly FONTS = 'system-ui, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji", -apple-system, "Segoe UI", Roboto, Ubuntu, Cantarell, "Noto Sans", sans-serif, BlinkMacSystemFont, "Helvetica Neue", Arial, "PingFang SC", "PingFang TC", "PingFang HK", "Microsoft Yahei", "Microsoft JhengHei"';
}

// ========== 指纹生成器辅助函数 ==========
export class FingerprintHelpers {
    /**
     * 加权随机选择
     */
    static weightedRandomChoice<T>(options: T[], weights: number[]): T {
        const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
        let random = Math.random() * totalWeight;

        for (let i = 0; i < options.length; i++) {
            random -= weights[i];
            if (random <= 0) {
                return options[i];
            }
        }
        return options[options.length - 1];
    }

    /**
     * 获取随机渲染器信息
     */
    static getRendererInfo(): { vendor: string; renderer: string } {
        const rendererStr = FingerprintData.GPU_VENDORS[Math.floor(Math.random() * FingerprintData.GPU_VENDORS.length)];
        const [vendor, renderer] = rendererStr.split('|');
        return { vendor, renderer };
    }

    /**
     * 获取屏幕配置
     */
    static getScreenConfig(): { width: number; height: number; availWidth: number; availHeight: number } {
        const resolutionStr = this.weightedRandomChoice(
            FingerprintData.SCREEN_RESOLUTIONS.resolutions,
            FingerprintData.SCREEN_RESOLUTIONS.weights
        );
        const [widthStr, heightStr] = resolutionStr.split(';');
        const width = parseInt(widthStr);
        const height = parseInt(heightStr);

        let availWidth: number, availHeight: number;

        if (Math.random() > 0.5) {
            availWidth = width - parseInt(String(this.weightedRandomChoice([0, 30, 60, 80], [0.1, 0.4, 0.3, 0.2])));
            availHeight = height;
        } else {
            availWidth = width;
            availHeight = height - parseInt(String(this.weightedRandomChoice([30, 60, 80, 100], [0.2, 0.5, 0.2, 0.1])));
        }

        return { width, height, availWidth, availHeight };
    }

    /**
     * 生成 Canvas 哈希
     */
    static generateCanvasHash(): string {
        return FingerprintData.CANVAS_HASH;
    }

    /**
     * 生成 WebGL 哈希
     */
    static generateWebglHash(): string {
        return crypto.createHash('md5').update(crypto.randomBytes(32)).digest('hex');
    }
}

// ========== ARC4 加密实现 ==========
export class ARC4 {
    private s: number[] = [];

    constructor(key: Uint8Array) {
        // 初始化 S 盒
        for (let i = 0; i < 256; i++) {
            this.s[i] = i;
        }

        // 密钥调度算法 (KSA)
        let j = 0;
        for (let i = 0; i < 256; i++) {
            j = (j + this.s[i] + key[i % key.length]) % 256;
            [this.s[i], this.s[j]] = [this.s[j], this.s[i]];
        }
    }

    /**
     * 加密数据
     */
    encrypt(data: Uint8Array): Uint8Array {
        const result = new Uint8Array(data.length);
        let i = 0, j = 0;

        for (let k = 0; k < data.length; k++) {
            i = (i + 1) % 256;
            j = (j + this.s[i]) % 256;
            [this.s[i], this.s[j]] = [this.s[j], this.s[i]];

            const keyByte = this.s[(this.s[i] + this.s[j]) % 256];
            result[k] = data[k] ^ keyByte;
        }

        return result;
    }
}

// ========== 指纹生成器 ==========
export class FingerprintGenerator {
    private config = CryptoConfig;
    private b1Key: Uint8Array;
    private encoder: Base64Encoder;

    constructor() {
        this.b1Key = new TextEncoder().encode("xhswebmplfbt");
        this.encoder = new Base64Encoder();
    }

    /**
     * 生成完整的浏览器指纹
     * 完全对应 Python 版本的实现
     */
    generate(cookies: Record<string, any>, userAgent: string): Record<string, any> {
        const cookieString = Object.entries(cookies).map(([k, v]) => `${k}=${v}`).join('; ');
        const screenConfig = FingerprintHelpers.getScreenConfig();
        const isIncognitoMode = FingerprintHelpers.weightedRandomChoice(["true", "false"], [0.95, 0.05]);
        const { vendor, renderer } = FingerprintHelpers.getRendererInfo();

        const x78Y = Math.floor(Math.random() * (2450 - 2350 + 1)) + 2350;

        const fp = {
            x1: userAgent,
            x2: "false",
            x3: "zh-CN",
            x4: String(FingerprintHelpers.weightedRandomChoice(
                FingerprintData.COLOR_DEPTH_OPTIONS.values,
                FingerprintData.COLOR_DEPTH_OPTIONS.weights
            )),
            x5: String(FingerprintHelpers.weightedRandomChoice(
                FingerprintData.DEVICE_MEMORY_OPTIONS.values,
                FingerprintData.DEVICE_MEMORY_OPTIONS.weights
            )),
            x6: "24",
            x7: `${vendor},${renderer}`,
            x8: String(FingerprintHelpers.weightedRandomChoice(
                FingerprintData.CORE_OPTIONS.values,
                FingerprintData.CORE_OPTIONS.weights
            )),
            x9: `${screenConfig.width};${screenConfig.height}`,
            x10: `${screenConfig.availWidth};${screenConfig.availHeight}`,
            x11: "-480",
            x12: "Asia/Shanghai",
            x13: isIncognitoMode,
            x14: isIncognitoMode,
            x15: isIncognitoMode,
            x16: "false",
            x17: "false",
            x18: "un",
            x19: "Win32",
            x20: "",
            x21: FingerprintData.BROWSER_PLUGINS,
            x22: FingerprintHelpers.generateWebglHash(),
            x23: "false",
            x24: "false",
            x25: "false",
            x26: "false",
            x27: "false",
            x28: "0,false,false",
            x29: "4,7,8",
            x30: "swf object not loaded",
            x33: "0",
            x34: "0",
            x35: "0",
            x36: String(Math.floor(Math.random() * 20) + 1),
            x37: "0|0|0|0|0|0|0|0|0|1|0|0|0|0|0|0|0|0|1|0|0|0|0|0",
            x38: "0|0|1|0|1|0|0|0|0|0|1|0|1|0|1|0|0|0|0|0|0|0|0|0|0|0|0|0|0|0|0|0|0|0|0|0|0|0|0",
            x39: 0,
            x40: "0",
            x41: "0",
            x42: "3.4.4",
            x43: FingerprintHelpers.generateCanvasHash(),
            x44: String(Math.floor(Date.now())),
            x45: "__SEC_CAV__1-1-1-1-1|__SEC_WSA__|",
            x46: "false",
            x47: "1|0|0|0|0|0",
            x48: "",
            x49: "{list:[],type:}",
            x50: "",
            x51: "",
            x52: "",
            x53: crypto.createHash('md5').update(crypto.randomBytes(32)).digest('hex'),
            x54: FingerprintData.VOICE_HASH_OPTIONS,
            x55: "380,380,360,400,380,400,420,380,400,400,360,360,440,420",
            x56: `${vendor}|${renderer}|${FingerprintHelpers.generateWebglHash()}|35`,
            x57: cookieString,
            x58: "180",
            x59: "2",
            x60: "63",
            x61: "1291",
            x62: "2047",
            x63: "0",
            x64: "0",
            x65: "0",
            x66: {
                referer: "",
                location: "https://www.xiaohongshu.com/explore",
                frame: 0
            },
            x67: "1|0",
            x68: "0",
            x69: "326|1292|30",
            x70: ["location"],
            x71: "true",
            x72: "complete",
            x73: "1191",
            x74: "0|0|0",
            x75: "Google Inc.",
            x76: "true",
            x77: "1|1|1|1|1|1|1|1|1|1",
            x78: {
                x: 0,
                y: x78Y,
                left: 0,
                right: 290.828125,
                bottom: x78Y + 18,
                height: 18,
                top: x78Y,
                width: 290.828125,
                font: FingerprintData.FONTS
            },
            x79: "144|599565058866",
            x80: "1|[object FileSystemDirectoryHandle]",
            x31: "124.04347527516074",
            x82: "_0x17a2|_0x1954"
        };

        return fp;
    }

    /**
     * 生成 b1 参数
     * 完全对应 Python 版本的实现
     */
    generateB1(fp: Record<string, any>): string {
        const b1Fp = {
            x33: fp.x33,
            x34: fp.x34,
            x35: fp.x35,
            x36: fp.x36,
            x37: fp.x37,
            x38: fp.x38,
            x39: fp.x39,
            x42: fp.x42,
            x43: fp.x43,
            x44: fp.x44,
            x45: fp.x45,
            x46: fp.x46,
            x48: fp.x48,
            x49: fp.x49,
            x50: fp.x50,
            x51: fp.x51,
            x52: fp.x52,
            x82: fp.x82
        };

        const b1Json = JSON.stringify(b1Fp, null, 0).replace(/\s+/g, '');

        // 使用 ARC4 加密
        const cipher = new ARC4(this.b1Key);
        const ciphertext = cipher.encrypt(new TextEncoder().encode(b1Json));

        // 转换为 Latin1 字符串并进行 URL 编码
        let latin1String = '';
        for (let i = 0; i < ciphertext.length; i++) {
            latin1String += String.fromCharCode(ciphertext[i]);
        }

        const encodedUrl = encodeURIComponent(latin1String).replace(/[!'()*~_-]/g, (c) => {
            return c; // 保持这些字符不编码
        });

        // 处理编码结果
        const b: number[] = [];
        const parts = encodedUrl.split('%').slice(1); // 去掉第一个空元素

        for (const part of parts) {
            const chars = part.split('');
            if (chars.length >= 2) {
                b.push(parseInt(chars.slice(0, 2).join(''), 16));
                for (let i = 2; i < chars.length; i++) {
                    b.push(chars[i].charCodeAt(0));
                }
            }
        }

        const b1 = this.encoder.encode(JSON.stringify(b));
        return b1;
    }

    /**
     * 更新指纹
     * 完全对应 Python 版本的实现
     */
    update(fp: Record<string, any>, cookies: Record<string, any>, url: string): void {
        const cookieString = Object.entries(cookies).map(([k, v]) => `${k}=${v}`).join('; ');

        fp.x39 = 0;
        fp.x44 = String(Math.floor(Date.now()));
        fp.x57 = cookieString;
        fp.x66 = {
            referer: "https://www.xiaohongshu.com/explore",
            location: url,
            frame: 0
        };
    }
}

// ========== x-s-common 签名器 ==========
export class XsCommonSigner {
    private config = CryptoConfig;
    private fpGenerator: FingerprintGenerator;
    private encoder: Base64Encoder;

    constructor() {
        this.fpGenerator = new FingerprintGenerator();
        this.encoder = new Base64Encoder();
    }

    sign(cookieDict: Record<string, any>): string {
        if (!cookieDict.a1) {
            throw new Error("Missing 'a1' in cookies");
        }

        const a1Value = cookieDict.a1;
        const fingerprint = this.fpGenerator.generate(cookieDict, this.config.PUBLIC_USERAGENT);
        const b1 = this.fpGenerator.generateB1(fingerprint);
        const x9 = CRC32.crc32JsInt(b1, true);

        const signStruct = {
            s0: 5,
            s1: "",
            x0: "1",
            x1: "4.2.6",
            x2: "Windows",
            x3: "xhs-pc-web",
            x4: "4.86.0",
            x5: a1Value,
            x6: "",
            x7: "",
            x8: b1,
            x9: x9,
            x10: 0,
            x11: "normal"
        };

        const signJson = JSON.stringify(signStruct);
        return this.encoder.encode(signJson);
    }
}

// ========== 主客户端类 ==========
export class Xhshow {
    private cryptoProcessor = new CryptoProcessor();
    private xsCommonSigner = new XsCommonSigner();

    private buildContentString(
        method: string,
        uri: string,
        payload: Record<string, any> | null = null
    ): string {
        payload = payload || {};

        if (method.toUpperCase() === 'POST') {
            // 使用紧凑格式的 JSON，与 Python json.dumps(separators=(",", ":"), ensure_ascii=False) 一致
            return uri + JSON.stringify(payload);
        } else {
            if (Object.keys(payload).length === 0) {
                return uri;
            } else {
                const params = Object.entries(payload).map(([key, value]) => {
                    let valueStr: string;
                    if (Array.isArray(value)) {
                        valueStr = value.map(v => String(v)).join(',');
                    } else {
                        valueStr = value !== null ? String(value) : '';
                    }
                    // XHS只编码等号为%3D
                    valueStr = valueStr.replace(/=/g, '%3D');
                    return `${key}=${valueStr}`;
                });
                return `${uri}?${params.join('&')}`;
            }
        }
    }

    private generateDValue(content: string): string {
        return crypto.createHash('md5').update(content, 'utf-8').digest('hex');
    }

    private buildSignature(
        dValue: string,
        a1Value: string,
        xsecAppid: string = "xhs-pc-web",
        stringParam: string = "",
        timestamp?: number
    ): string {
        const payloadArray = this.cryptoProcessor.buildPayloadArray(
            dValue, a1Value, xsecAppid, stringParam, timestamp
        );

        const xorResult = this.cryptoProcessor.bitOps.xorTransformArray(payloadArray);

        return this.cryptoProcessor.b64encoder.encodeX3(xorResult.slice(0, 124));
    }

    signXs(
        method: HttpMethod,
        uri: string,
        a1Value: string,
        xsecAppid: string = "xhs-pc-web",
        payload: Record<string, any> | null = null,
        timestamp?: number
    ): string {
        const validatedMethod = RequestSignatureValidator.validateMethod(method);
        const validatedUri = RequestSignatureValidator.validateUri(uri);
        const validatedA1Value = RequestSignatureValidator.validateA1Value(a1Value);
        const validatedXsecAppid = RequestSignatureValidator.validateXsecAppid(xsecAppid);
        const validatedPayload = RequestSignatureValidator.validatePayload(payload);

        // 深拷贝模板，避免修改原始配置对象
        const signatureData = JSON.parse(JSON.stringify(this.cryptoProcessor.config.SIGNATURE_DATA_TEMPLATE));

        const content = this.buildContentString(validatedMethod, validatedUri, validatedPayload);

        const dValue = this.generateDValue(content);
        signatureData.x3 = this.cryptoProcessor.config.X3_PREFIX + this.buildSignature(
            dValue, validatedA1Value, validatedXsecAppid, content, timestamp
        );

        // JSON.stringify 默认就是紧凑格式，与 Python 的 separators=(",", ":") 一致
        const signatureJson = JSON.stringify(signatureData);
        return this.cryptoProcessor.config.XYS_PREFIX + this.cryptoProcessor.b64encoder.encode(signatureJson);
    }

    signXsGet(
        uri: string,
        a1Value: string,
        xsecAppid: string = "xhs-pc-web",
        params: Record<string, any> | null = null,
        timestamp?: number
    ): string {
        return this.signXs('GET', uri, a1Value, xsecAppid, params, timestamp);
    }

    signXsPost(
        uri: string,
        a1Value: string,
        xsecAppid: string = "xhs-pc-web",
        payload: Record<string, any> | null = null,
        timestamp?: number
    ): string {
        return this.signXs('POST', uri, a1Value, xsecAppid, payload, timestamp);
    }

    signXsCommon(cookieDict: Record<string, any> | string): string {
        const cookies = this.parseCookies(cookieDict);
        return this.xsCommonSigner.sign(cookies);
    }

    signXsc(cookieDict: Record<string, any> | string): string {
        return this.signXsCommon(cookieDict);
    }

    private parseCookies(cookies: Record<string, any> | string): Record<string, any> {
        if (typeof cookies === 'string') {
            const cookieObj: Record<string, any> = {};
            cookies.split(';').forEach(item => {
                const [key, value] = item.trim().split('=');
                if (key && value) {
                    cookieObj[key] = value;
                }
            });
            return cookieObj;
        }
        return cookies;
    }

    signHeaders(
        method: HttpMethod,
        uri: string,
        cookies: Record<string, any> | string,
        xsecAppid: string = "xhs-pc-web",
        params?: Record<string, any> | null,
        payload?: Record<string, any> | null,
        timestamp?: number
    ): Record<string, string> {
        if (timestamp === undefined) {
            timestamp = Date.now() / 1000;
        }

        const methodUpper = method.toUpperCase();

        // 验证方法和参数
        let requestData: Record<string, any> | null = null;
        if (methodUpper === 'GET') {
            if (payload !== undefined && payload !== null) {
                throw new Error("GET requests must use 'params', not 'payload'");
            }
            requestData = params || null;
        } else if (methodUpper === 'POST') {
            if (params !== undefined && params !== null) {
                throw new Error("POST requests must use 'payload', not 'params'");
            }
            requestData = payload || null;
        } else {
            throw new Error(`Unsupported method: ${method}`);
        }

        const cookieDict = this.parseCookies(cookies);

        const a1Value = cookieDict.a1;
        if (!a1Value) {
            throw new Error("Missing 'a1' in cookies");
        }

        const xS = this.signXs(methodUpper as HttpMethod, uri, a1Value, xsecAppid, requestData, timestamp);
        const xSCommon = this.signXsCommon(cookieDict);
        const xT = this.getXT(timestamp);
        const xB3Traceid = this.getB3TraceId();
        const xXrayTraceid = this.getXrayTraceId(Math.floor(timestamp * 1000));

        return {
            'x-s': xS,
            'x-s-common': xSCommon,
            'x-t': xT.toString(),
            'x-b3-traceid': xB3Traceid,
            'x-xray-traceid': xXrayTraceid
        };
    }

    signHeadersGet(
        uri: string,
        cookies: Record<string, any> | string,
        xsecAppid: string = "xhs-pc-web",
        params?: Record<string, any> | null,
        timestamp?: number
    ): Record<string, string> {
        return this.signHeaders('GET', uri, cookies, xsecAppid, params, undefined, timestamp);
    }

    signHeadersPost(
        uri: string,
        cookies: Record<string, any> | string,
        xsecAppid: string = "xhs-pc-web",
        payload?: Record<string, any> | null,
        timestamp?: number
    ): Record<string, string> {
        return this.signHeaders('POST', uri, cookies, xsecAppid, undefined, payload, timestamp);
    }

    decodeX3(x3Signature: string): Uint8Array {
        if (x3Signature.startsWith(this.cryptoProcessor.config.X3_PREFIX)) {
            x3Signature = x3Signature.substring(this.cryptoProcessor.config.X3_PREFIX.length);
        }

        const decodedBytes = this.cryptoProcessor.b64encoder.decodeX3(x3Signature);
        return this.cryptoProcessor.bitOps.xorTransformArray(Array.from(decodedBytes));
    }

    decodeXs(xsSignature: string): Record<string, any> {
        if (xsSignature.startsWith(this.cryptoProcessor.config.XYS_PREFIX)) {
            xsSignature = xsSignature.substring(this.cryptoProcessor.config.XYS_PREFIX.length);
        }

        const jsonString = this.cryptoProcessor.b64encoder.decode(xsSignature);
        try {
            return JSON.parse(jsonString);
        } catch (e) {
            throw new Error(`Invalid signature: JSON decode failed - ${e}`);
        }
    }

    getB3TraceId(): string {
        return this.cryptoProcessor.randomGen.generateB3TraceId();
    }

    getXrayTraceId(timestamp?: number, seq?: number): string {
        return this.cryptoProcessor.randomGen.generateXrayTraceId(timestamp, seq);
    }

    getXT(timestamp?: number): number {
        if (timestamp === undefined) {
            timestamp = Date.now() / 1000;
        }
        return Math.floor(timestamp * 1000);
    }

    buildUrl(baseUrl: string, params?: Record<string, any>): string {
        if (!params || Object.keys(params).length === 0) {
            return baseUrl;
        }

        const queryString = Object.entries(params)
            .map(([key, value]) => {
                const valueStr = Array.isArray(value) ? value.join(',') : String(value);
                return `${key}=${encodeURIComponent(valueStr)}`;
            })
            .join('&');

        return `${baseUrl}?${queryString}`;
    }

    buildJsonBody(payload: Record<string, any>): string {
        // JSON.stringify 默认就是紧凑格式，与 Python json.dumps(separators=(",", ":"), ensure_ascii=False) 一致
        return JSON.stringify(payload);
    }
}

// 导出默认实例
export default new Xhshow();

